import type { DashboardResponse } from "@rbim/shared";
import { Bot, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { ApiRequestError, apiRequest } from "../api";
import { LiraChat } from "../features/lira/lira-chat";

export function LiraAssistantPage() {
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [barangayId, setBarangayId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    apiRequest<DashboardResponse>("/dashboard")
      .then((response) => { if (active) setDashboard(response); })
      .catch((requestError) => {
        if (active) setError(requestError instanceof ApiRequestError ? requestError.message : "LIRA context could not be loaded.");
      });
    return () => { active = false; };
  }, []);

  const fixedBarangayId = dashboard?.scope.type === "BARANGAY"
    ? dashboard.scope.barangayId
    : null;
  const activeBarangayId = fixedBarangayId ?? barangayId;
  const contextBarangay = dashboard?.barangays.find((barangay) => barangay.barangayId === activeBarangayId) ?? null;
  const isBarangayScope = fixedBarangayId !== null;
  const isProvinceScope = dashboard?.scope.type === "PROVINCE";
  const broadScopeLabel = isProvinceScope ? "Province" : "Municipality/City";
  const broadScopeContext = isProvinceScope
    ? `Province context: ${dashboard?.locationLabel ?? "Assigned Province"}`
    : "Municipality-wide context";

  return (
    <section className="portal-page lira-page">
      <header className="page-heading">
        <div>
          <span className="eyebrow">Secure RBIM assistance</span>
          <h1>LIRA AI Assistant</h1>
          <p>Ask about authorized RBIM statistics, population concepts, migration, demographics, and related local population planning.</p>
        </div>
        <span className="status-pill"><Bot aria-hidden="true" />Available</span>
      </header>
      {error ? <div className="form-error" role="alert">{error}</div> : null}
      <div className="lira-page-grid">
        <aside className="lira-scope-card">
          <ShieldCheck aria-hidden="true" />
          <h2>Authorized context</h2>
          <p>
            {isBarangayScope
              ? "LIRA is restricted to the Barangay assigned to your secure server session."
              : isProvinceScope
                ? "LIRA is restricted to the Province assigned to your secure server session, including only its authorized municipalities, cities, and Barangays."
                : "LIRA derives Municipality/City access from your secure server session. Browser-supplied locality IDs are never trusted."}
          </p>
          <label htmlFor={isBarangayScope ? undefined : "lira-barangay-context"}>Conversation scope</label>
          {isBarangayScope ? (
            <span className="status-pill">{dashboard?.locationLabel ?? "Assigned Barangay"}</span>
          ) : (
            <select id="lira-barangay-context" value={barangayId} onChange={(event) => setBarangayId(event.currentTarget.value)}>
              <option value="">Entire {broadScopeLabel}</option>
              {dashboard?.barangays.map((barangay) => <option key={barangay.barangayId} value={barangay.barangayId}>{barangay.barangayName}</option>)}
            </select>
          )}
          <small>
            {isBarangayScope
              ? "Questions and answers remain limited to this Barangay. Resident names and other personally identifiable information are not provided through LIRA."
              : isProvinceScope
                ? "You can ask province-wide questions or narrow the conversation to an authorized Barangay. Resident identities remain protected."
                : "You can ask municipality-wide questions or narrow the conversation to an authorized Barangay. Resident identities remain protected."}
          </small>
        </aside>
        <LiraChat
          contextBarangay={contextBarangay}
          contextLabel={contextBarangay ? undefined : fixedBarangayId
            ? `Barangay context: ${dashboard?.locationLabel ?? "Assigned Barangay"}`
            : broadScopeContext}
          {...(fixedBarangayId ? { contextBarangayId: fixedBarangayId } : {})}
        />
      </div>
    </section>
  );
}
